import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { aggregateActivityData, fetchAllEntryConfigs, fetchAllTasksForTraitSearch } from '@/services/adminService';
import { TraitSearchData, TraitSearchResult } from '@/types/adminActivity';

export function useTraitSearch(enabled: boolean) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: allEntryConfigs,
    isLoading: isLoadingConfigs,
    error: configsError,
  } = useQuery<TraitSearchResult[], Error>({
    queryKey: ['adminAllEntryConfigs'],
    queryFn: fetchAllEntryConfigs,
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: allTasks,
    isLoading: isLoadingTasks,
    error: tasksError,
  } = useQuery({
    queryKey: ['adminAllTasksForTraitSearch'],
    queryFn: fetchAllTasksForTraitSearch,
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const searchData = useMemo<TraitSearchData | null>(() => {
    if (!allEntryConfigs || !allTasks || !debouncedTerm.trim()) return null;

    const term = debouncedTerm.toLowerCase();

    // Find matching traits by name substring
    const matchingTraits = allEntryConfigs.filter(t =>
      t.name.toLowerCase().includes(term)
    );

    // Build set of matching trait paths for fast lookup
    const matchingPaths = new Set(matchingTraits.map(t => t.path));

    // Filter tasks that reference any matching trait
    const matchingTasks = allTasks.filter(task =>
      task.entryConfigPaths.some(p => matchingPaths.has(p))
    );

    const { organizationActivities } = aggregateActivityData(matchingTasks);

    return {
      matchingTraits,
      matchingTasks,
      organizationActivities,
    };
  }, [allEntryConfigs, allTasks, debouncedTerm]);

  return {
    searchTerm,
    setSearchTerm,
    searchData,
    isLoading: isLoadingConfigs || isLoadingTasks,
    error: configsError || tasksError,
    matchCount: searchData?.matchingTasks.length ?? 0,
    traitMatchCount: searchData?.matchingTraits.length ?? 0,
  };
}
